import React, {useContext, useEffect, useRef, useState} from 'react';
import ConfirmDialogTemplate from '@/Components/Templates/ConfirmDialogTemplate';
import {fieldsPlaceholders} from '@/Config/forms';
import {Button} from 'primereact/button';
import {getFormErrorMessage, isFormFieldValid, validateForm} from '@/Helpers/forms';
import NumberInputTemplate from '@/Components/Templates/Forms/NumberInputTemplate';
import {apiRequest, formatBigNo, toast} from '@/Helpers/general';
import {AppContext} from '@/Components/Context/AppContext';
import {CaseContext} from '@/Components/Context/CaseContext';
import {ConfirmDialog, confirmDialog} from "primereact/confirmdialog";
import {Toast} from "primereact/toast";
import TextareaInputTemplate from "@/Components/Templates/Forms/TextareaInputTemplate";
import styled from "styled-components";

const OutcomeAmountsModalWrap = styled.div`
    .disabled {
        opacity: 0.2%;
        pointer-events: none;
    }
`;

/**
 * Component which renders a modal for editing the outcome amounts
 */
const OutcomeAmountsModal = ({visible, setVisible, loadOutcomes}) => {
    const {toastRef} = useContext(AppContext);
    const {caseId, getCaseData, outcomeDays, outcomeAmount, setOutcomeDays, setOutcomeAmount, requestedOutcomeDays, requestedOutcomeAmount} = useContext(CaseContext);

    const [daysNo, setDaysNo] = useState(0);
    const [amountPerDay, setAmountPerDay] = useState(0);
    const [formErrors, setFormErrors] = useState({});
    const [formLoading, setFormLoading] = useState(false);
    const [rationale, setRationale] = useState('');
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    const formValidationRules = [
        {
            field: 'total_hire_period_offered',
            rules: [{name: 'required'}]
        },
        {
            field: 'hire_daily_rate_offered',
            rules: [{name: 'required'}]
        },
        {
            field: 'rationale',
            rules: [
                {name: 'required'},
                {
                    name: 'minLength',
                    params: {
                        length: 10
                    }
                }
            ]
        }
    ];

    const confirmContent = () => {
        return <>
            <div className="flex flex-column align-items-center w-full gap-3 border-bottom-1 surface-border">
                <TextareaInputTemplate
                    name="rationale"
                    label="Argue your change"
                    placeholder={fieldsPlaceholders.text}
                    value={rationale}
                    isValid={isFormFieldValid(formErrors, 'rationale')}
                    errorMessage={getFormErrorMessage(formErrors, 'rationale')}
                    onChangeFunction={(name, value) => setRationale(value)}
                />
            </div>
        </>
    }

    const modalContent = () => {
        return (
            <>
                <div className="auto-height px-3">
                    <table className="gray-table">
                        <thead>
                        <tr>
                            <td></td>
                            <td>CHO REQUESTED PAYMENT</td>
                            <td>NIGHTINGALE RESPONSE</td>
                            <td>SAVINGS</td>
                        </tr>
                        </thead>

                        <tbody>
                        <tr>
                            <td>Total Hire Period</td>
                            <td>{requestedOutcomeDays} days</td>
                            <td>
                                <NumberInputTemplate
                                    name="total_hire_period_offered"
                                    // label="Total Hire Period Offered"
                                    placeholder={fieldsPlaceholders.integer}
                                    labelClassName="one-line"
                                    value={daysNo}
                                    bottomMargin={3}
                                    onChangeFunction={(name, value) => setDaysNo(value)}
                                    isValid={isFormFieldValid(formErrors, 'total_hire_period_offered')}
                                    errorMessage={getFormErrorMessage(formErrors, 'total_hire_period_offered')}
                                    noMargins
                                />
                            </td>
                            <td>{requestedOutcomeDays - daysNo} days</td>
                        </tr>

                        <tr>
                            <td>Total Daily Rate</td>
                            <td>£ {formatBigNo(requestedOutcomeAmount)}</td>
                            <td>
                                <NumberInputTemplate
                                    name="hire_daily_rate_offered"
                                    // label="Hire Daily Rate Offered"
                                    placeholder={fieldsPlaceholders.integer}
                                    labelClassName="one-line"
                                    value={amountPerDay}
                                    mode={'decimal'}
                                    bottomMargin={3}
                                    format
                                    onChangeFunction={(name, value) => setAmountPerDay(value)}
                                    isValid={isFormFieldValid(formErrors, 'hire_daily_rate_offered')}
                                    errorMessage={getFormErrorMessage(formErrors, 'hire_daily_rate_offered')}
                                    noMargins
                                />
                            </td>
                            <td>£ {formatBigNo(requestedOutcomeAmount - amountPerDay)}</td>
                        </tr>
                        </tbody>

                        <tfoot>
                        <tr>
                            <td>Total</td>
                            <td>£ {formatBigNo(requestedOutcomeDays * requestedOutcomeAmount)}</td>
                            <td>£ {formatBigNo(daysNo * amountPerDay)}</td>
                            <td>£ {formatBigNo((requestedOutcomeDays * requestedOutcomeAmount) - (daysNo * amountPerDay))}</td>
                        </tr>
                        </tfoot>
                    </table>
                </div>
            </>)
    };

    /**
     * Save the modified data in the outcomes as well
     */
    const saveOutcome = (formData) => {
        apiRequest('post', 'case/' + caseId + '/outcomes', {
            data: formData,
            onSuccess: (response) => {
                if (response?.data?.responseType == 'success') {
                    setFormLoading(false);
                    setConfirmModalOpen(false)
                    setVisible(false);
                    getCaseData();
                    loadOutcomes();

                    toast(toastRef, 'success', 'Outcome amounts have been updated.');

                } else {
                    toast(toastRef, 'error', 'Error saving outcome amounts.');
                }
            },
            onFailure: (error) => {
                setFormLoading(false);

                if (error.status == 422) {
                    if (error.messages) {
                        let errorMessages = error.messages,
                            newFormErrors = {};

                        for (let key in errorMessages) {
                            if (Object.hasOwnProperty.call(errorMessages, key)) {
                                newFormErrors[key] = errorMessages[key];
                            }
                        }

                        setFormErrors(newFormErrors);
                    } else {
                        toast(toastRef, 'error', 'Error saving outcome amounts.');
                    }

                } else if (error.status == 403) {
                    toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');

                } else {
                    toast(toastRef, 'error', 'Error saving outcome amounts.');
                }
            }
        });
    };

    /**
     * Submits the edit amounts form
     */
    const submitForm = async () => {
        setFormLoading(true);

        let formData = {
                total_hire_period_offered: daysNo,
                hire_daily_rate_offered: amountPerDay,
                rationale: rationale
            },
            validation = validateForm(formValidationRules, formData, setFormErrors);

        if (validation) {
            saveOutcome(formData);

        } else {
            setFormLoading(false);
        }
    };

    const widgetFooter = (
        <>
            <hr/>

            <div className="text-right">
                <Button label="Save"
                        onClick={() => setConfirmModalOpen(true)}
                        loading={formLoading} loadingIcon="pi pi-spin pi-sun" iconPos="center"/>
            </div>
        </>
    );

    useEffect(() => {
        setDaysNo(outcomeDays);
    }, [outcomeDays]);


    useEffect(() => {
        setAmountPerDay(outcomeAmount);
    }, [outcomeAmount]);

    return (
        <>
            {!!visible && <ConfirmDialogTemplate
                title={'Edit Outcome Amounts'}
                className="modal-tabs"
                message={modalContent()}
                onConfirm={() => {
                }}
                visible={visible}
                setVisible={setVisible}
                onHide={() => setVisible(false)}
                footer={widgetFooter}
                noIcon
            />}
            {!!confirmModalOpen && <ConfirmDialogTemplate
                title="Edit Outcome Confirmation"
                message={confirmContent()}
                onConfirm={submitForm}
                visible={confirmModalOpen}
                setVisible={setConfirmModalOpen}
                onHide={() => setConfirmModalOpen(false)}
                noIcon
            />}
        </>
    );
};

export default OutcomeAmountsModal;
